/**
 * WipStatusBanner — J3-A
 *
 * Header strip displayed at the top of ControlTowerPage.
 * Shows WIP counts per status bucket with colour-coded fill indicators.
 *
 * Compliance: LEAN-01 (WIP limits visible), LEAN-07 (Pull over Push).
 * PRD §12.3.6 task 2.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';
import { useRorosWipStatus } from '@/hooks/roros/control-tower';
import { Spinner } from '@/components/ui/Spinner/Spinner';

// ─── Status bucket display config ─────────────────────────────────────────────

interface StatusBucket {
  key: string;
  label: string;
}

const WIP_STATUS_BUCKETS: StatusBucket[] = [
  { key: 'backlog',               label: 'Backlog' },
  { key: 'ready',                 label: 'Ready' },
  { key: 'in_progress',           label: 'In Progress' },
  { key: 'done',                  label: 'Done' },
  { key: 'quality_confirmation',  label: 'QC' },
];

// ─── Colour thresholds ──────────────────────────────────────────────────────

/**
 * Returns the Tailwind colour classes for count/limit indicator.
 * - green  : count < limit * 0.8
 * - amber  : count >= limit * 0.8 (approaching limit)
 * - red    : count == limit       (at limit — pull blocked)
 */
function getBucketColour(count: number, limit: number | null): {
  bar: string;
  text: string;
  bg: string;
} {
  if (limit === null || limit === 0) {
    // No limit configured — neutral blue
    return { bar: 'bg-blue-400', text: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' };
  }
  if (count >= limit) {
    return { bar: 'bg-red-500',   text: 'text-red-700',   bg: 'bg-red-50 border-red-200' };
  }
  if (count >= limit * 0.8) {
    return { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
  }
  return { bar: 'bg-green-400',   text: 'text-green-700', bg: 'bg-green-50 border-green-200' };
}

// ─── BucketPill ────────────────────────────────────────────────────────────

interface BucketPillProps {
  label: string;
  count: number;
  limit: number | null;
}

function BucketPill({ label, count, limit }: BucketPillProps) {
  const colours = getBucketColour(count, limit);
  const fillPct = limit ? Math.min(1, count / limit) * 100 : 0;

  return (
    <div
      className={cn(
        'relative flex flex-col items-center px-3 py-2 rounded-lg border text-center min-w-[90px] overflow-hidden',
        colours.bg,
      )}
      title={
        limit
          ? `${label}: ${count} / ${limit}`
          : `${label}: ${count} (no limit)`
      }
    >
      {/* Fill bar */}
      {limit !== null && (
        <div
          className={cn('absolute bottom-0 left-0 h-[3px]', colours.bar)}
          style={{ width: `${fillPct}%`, transition: 'width 0.4s ease' }}
        />
      )}

      <span className="text-xs font-medium text-muted-foreground leading-none mb-1">
        {t(label)}
      </span>
      <span className={cn('text-base font-bold leading-none', colours.text)}>
        {count}
        {limit !== null && (
          <span className="text-xs font-normal ml-0.5">/{limit}</span>
        )}
      </span>
    </div>
  );
}

// ─── WipStatusBanner ──────────────────────────────────────────────────────────

export function WipStatusBanner() {
  const { data, isLoading, isError } = useRorosWipStatus();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
        <Spinner size="sm" />
        <span>{t('Loading WIP status...')}</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-xs text-muted-foreground px-1 py-2">
        {t('WIP status unavailable')}
      </div>
    );
  }

  const { statusCounts = {}, limits = {} } = data;

  return (
    <div
      className="flex flex-wrap items-center gap-2 py-2"
      role="status"
      aria-label={t('WIP status overview')}
    >
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mr-1">
        {t('WIP')}
      </span>
      {WIP_STATUS_BUCKETS.map(({ key, label }) => (
        <BucketPill
          key={key}
          label={label}
          count={statusCounts[key] ?? 0}
          limit={limits[key] ?? null}
        />
      ))}
    </div>
  );
}
